import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { AlertTriangle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Tiny banner that fetches the Live Ops `low_stock` block and renders compact
 * urgent/warning chips at the top of any admin tab. Auto-hides when nothing
 * is low.
 *
 * Drop-in:  <LowStockBanner />
 */
const LowStockBanner = ({ refreshMs = 30000 }) => {
  const [low, setLow] = useState(null);

  const fetchLow = useCallback(async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/live-ops/dashboard`, { withCredentials: true });
      setLow(r.data.low_stock);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchLow();
    const t = setInterval(fetchLow, refreshMs);
    return () => clearInterval(t);
  }, [fetchLow, refreshMs]);

  if (!low || (low.urgent.length === 0 && low.warning.length === 0)) return null;

  return (
    <div className="space-y-2" data-testid="low-stock-banner">
      {low.urgent.length > 0 && (
        <div className="bg-red-50 border-2 border-red-300 rounded p-3 flex items-start gap-2" data-testid="low-stock-banner-urgent">
          <AlertTriangle className="w-5 h-5 text-red-700 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-xs font-bold text-red-900 mb-1">
              URGENT — {low.urgent.length} item{low.urgent.length === 1 ? '' : 's'} out or near-empty
            </p>
            <div className="flex flex-wrap gap-1.5">
              {low.urgent.map(i => (
                <span key={i.id} className="text-[11px] font-mono bg-white border border-red-300 text-red-800 px-1.5 py-0.5 rounded">
                  {i.name}: <strong>{i.remaining === 0 ? 'OUT' : `${i.remaining} left`}</strong>
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
      {low.warning.length > 0 && (
        <div className="bg-amber-50 border border-amber-300 rounded p-2.5 flex items-start gap-2" data-testid="low-stock-banner-warning">
          <AlertTriangle className="w-4 h-4 text-amber-700 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-xs font-semibold text-amber-900 mb-1">
              Low stock — {low.warning.length} item{low.warning.length === 1 ? '' : 's'} below threshold
            </p>
            <div className="flex flex-wrap gap-1.5">
              {low.warning.map(i => (
                <span key={i.id} className="text-[11px] font-mono bg-white border border-amber-300 text-amber-800 px-1.5 py-0.5 rounded">
                  {i.name}: <strong>{i.remaining} left</strong>
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LowStockBanner;
